import { Alert, Item, ItemContent, ItemHeader, ItemTitle, ScrollArea, Skeleton, Tooltip } from '@ibsys/kit';
import { LockIcon } from 'lucide-react';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useKmEntityTypes } from '@/entities/ontology';
import { useMainLayout } from '@/shared/ui';

type Props = {
  search: string;
};

/**
 * Левая панель: список типов сущностей с клиентской фильтрацией по названию и коду.
 */
export const OntologyList: FC<Props> = ({ search }) => {
  const { t } = useTranslation('ontology');
  const { code } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const { isMobile } = useMainLayout();

  const { data, isLoading } = useKmEntityTypes({ includeDeleted: false, limit: 1000 });

  const items = useMemo(() => data?.items ?? [], [data]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter(
      (item) => item.name.toLowerCase().includes(query) || item.code.toLowerCase().includes(query),
    );
  }, [items, search]);

  // Автонавигация на первый тип на широких экранах, пока тип не выбран
  useEffect(() => {
    if (!code && !isMobile && items.length > 0 && !isLoading) {
      navigate(`/ontology/${items[0].code}`, { replace: true });
    }
  }, [code, isMobile, items, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 h-full">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  if (!filtered.length) {
    return <Alert type="info" className="text-muted-foreground p-4" title={t('listIsEmpty')} />;
  }

  return (
    <ScrollArea>
      <ul className="flex flex-col gap-4">
        {filtered.map((item) => {
          const isActive = item.code === code;
          return (
            <li
              key={item.code}
              role="button"
              tabIndex={-1}
              onClick={() => navigate(`/ontology/${item.code}`)}
            >
              <Item variant={isActive ? 'muted' : 'outline'} className="hover:bg-muted cursor-pointer">
                <ItemHeader>
                  <ItemTitle className="flex flex-wrap items-center gap-2">
                    <span className="whitespace-normal break-words">{item.name}</span>
                    {/* Системный тип — иконкой (правка запрещена), не текстовой колонкой */}
                    {item.isSystem && (
                      <Tooltip tooltip={t('systemType')}>
                        <LockIcon className="size-3.5 text-muted-foreground shrink-0" aria-label={t('systemType')} />
                      </Tooltip>
                    )}
                  </ItemTitle>
                </ItemHeader>
                <ItemContent>
                  {/* В левой панели — только код; описание и счётчик атрибутов показываем в детальной карточке справа */}
                  <span className="font-mono text-sm text-muted-foreground break-all">{item.code}</span>
                </ItemContent>
              </Item>
            </li>
          );
        })}
      </ul>
    </ScrollArea>
  );
};
